import React, { useContext, useEffect, useState } from "react"
import CommonModal from "../CommonModal";
import Button from "../../Elements/Button";
import CustomForm from "../../CommonComponents/CustomForm/CustomForm";
import { csrfToken } from "../../../utils/csrfTokenUtils";
import { networkService } from "../../config/axios";
import UserInfoContext from "../../../utils/userInfoContext";
import ModelVersionSelect, { defaultModelId } from "../../Pages/Admin/ModelRouting/ModelVersionSelect";

const AddModelRoutingModal = ({ openModal, closeModal, taskTypes, modelFamilies, companies, isSuperAdmin, currentCompanyName, onCreated }) => {
    const { setShowNotifications } = useContext(UserInfoContext)
    const emptyForm = () => ({
        scope_type: isSuperAdmin ? 'task' : 'client_task',
        task_type: '',
        client_id: '',
        model_id: defaultModelId(modelFamilies),
    })
    const [form, setForm] = useState(emptyForm)
    const [error, setError] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)

    useEffect(() => {
        if (openModal) {
            setForm(emptyForm())
            setError('')
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openModal, isSuperAdmin, modelFamilies])

    const handleChange = ({ target: { name, value } }) => {
        setError('')
        setForm(prev => ({
            ...prev,
            [name]: value,
            ...(name === 'scope_type' && value === 'global' ? { task_type: '', client_id: '' } : {}),
            ...(name === 'scope_type' && value === 'task' ? { client_id: '' } : {}),
        }))
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        if (form.scope_type !== 'global' && !form.task_type) {
            setError('Please select a task')
            return
        }
        if (isSuperAdmin && form.scope_type === 'client_task' && !form.client_id) {
            setError('Please select a client')
            return
        }
        if (!form.model_id) {
            setError('Please select a model')
            return
        }

        setIsSubmitting(true)
        try {
            const response = await networkService('admin/model_routing_configs.json', 'POST', {
                authenticity_token: csrfToken(),
                model_routing_config: {
                    scope_type: form.scope_type,
                    task_type: form.scope_type === 'global' ? 'global' : form.task_type,
                    client_id: form.scope_type === 'client_task' ? form.client_id : null,
                    model_id: form.model_id,
                    active: true,
                }
            })

            if (response.status === 201) {
                onCreated()
                closeModal()
                setShowNotifications({
                    showToast: true,
                    toastType: "success",
                    toastMessage: "Model routing config added",
                })
            }
        } catch (err) {
            setError(err?.response?.data?.errors?.join(', ') || 'Something went wrong')
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <CommonModal
            title="Add Model Routing Override"
            modalStatus={openModal}
            changeActiveStatus={() => closeModal()}
            classList="header-profile-modal-wrapper"
            centered
        >
            <CustomForm>
                {isSuperAdmin ? (
                    <div className="admin-form-group">
                        <label className="admin-form-label">Scope</label>
                        <select name="scope_type" value={form.scope_type} onChange={handleChange} className="admin-form-input">
                            <option value="task">Task default (all clients)</option>
                            <option value="client_task">Client override (one client, one task)</option>
                            <option value="global">Global fallback (every task)</option>
                        </select>
                    </div>
                ) : (
                    <p className="admin-form-hint">
                        This will add an override for your company{currentCompanyName ? ` (${currentCompanyName})` : ''} only.
                    </p>
                )}

                {form.scope_type !== 'global' && (
                    <div className="admin-form-group">
                        <label className="admin-form-label">
                            Task <span className="text-danger ms-1">*</span>
                        </label>
                        <select name="task_type" value={form.task_type} onChange={handleChange} className="admin-form-input">
                            <option value="">Select task</option>
                            {taskTypes?.map(t => (
                                <option key={t} value={t}>{t}</option>
                            ))}
                        </select>
                    </div>
                )}

                {isSuperAdmin && form.scope_type === 'client_task' && (
                    <div className="admin-form-group">
                        <label className="admin-form-label">
                            Client <span className="text-danger ms-1">*</span>
                        </label>
                        <select name="client_id" value={form.client_id} onChange={handleChange} className="admin-form-input">
                            <option value="">Select client</option>
                            {companies?.map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                    </div>
                )}

                <div className="admin-form-group">
                    <label className="admin-form-label">
                        Model <span className="text-danger ms-1">*</span>
                    </label>
                    <ModelVersionSelect
                        modelFamilies={modelFamilies}
                        value={form.model_id}
                        onChange={(model_id) => setForm(prev => ({ ...prev, model_id }))}
                    />
                </div>

                {error && <p className="admin-form-error">{error}</p>}

                <div className="d-flex justify-content-end mt-3">
                    <Button
                        text={isSubmitting ? 'Saving...' : 'Add'}
                        classList="success"
                        type="submit"
                        disabled={isSubmitting}
                        onClick={handleSubmit}
                    />
                </div>
            </CustomForm>
        </CommonModal>
    )
}
export default AddModelRoutingModal
